import { useCallback, useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../../../config/runtime';
import { trackEvent } from '../../../utils/analytics';

const MAX_MP4_BYTES = 25 * 1024 * 1024;

export function useChemistryMp4() {
  const requestRef = useRef(null);
  const objectUrlRef = useRef('');
  const mountedRef = useRef(true);
  const [conversionStatus, setConversionStatus] = useState('idle');
  const [conversionError, setConversionError] = useState('');
  const [mp4VideoUrl, setMp4VideoUrl] = useState('');
  const [mp4ExpiresAt, setMp4ExpiresAt] = useState('');
  const [mp4File, setMp4File] = useState(null);
  const [mp4Blob, setMp4Blob] = useState(null);

  const resetMp4 = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = '';
    setConversionStatus('idle');
    setConversionError('');
    setMp4VideoUrl('');
    setMp4ExpiresAt('');
    setMp4File(null);
    setMp4Blob(null);
  }, []);

  const convertToMp4 = useCallback(async (webmFile) => {
    if (!webmFile) return false;
    resetMp4();
    const controller = new AbortController();
    requestRef.current = controller;
    setConversionStatus('uploading');
    trackEvent('chemistry_video_convert_start', { analysis_type: 'chemistry' });
    let phase = 'upload';
    try {
      const form = new FormData();
      form.append('video', webmFile, 'chemistry-video.webm');
      const response = await fetch(buildApiUrl('/api/chemistry/video/convert'), {
        method: 'POST', body: form, signal: controller.signal,
      });
      let payload = {};
      try { payload = await response.json(); } catch { payload = {}; }
      if (!response.ok) {
        phase = response.status === 413 || response.status === 415 ? 'upload' : 'conversion';
        const error = new Error(payload.error_code || 'mp4_conversion_failed');
        error.status = response.status;
        throw error;
      }
      setConversionStatus('converting');
      phase = 'download';
      const downloadUrl = buildApiUrl(payload.download_url);
      const mp4Response = await fetch(downloadUrl, { signal: controller.signal, cache: 'no-store' });
      if (!mp4Response.ok) {
        const error = new Error(mp4Response.status === 410 ? 'mp4_expired' : 'mp4_download_failed');
        error.status = mp4Response.status;
        throw error;
      }
      const mimeType = String(mp4Response.headers.get('content-type') || '').split(';')[0];
      if (mimeType !== 'video/mp4') throw new Error('invalid_mp4_response');
      const blob = await mp4Response.blob();
      if (!blob.size || blob.size > MAX_MP4_BYTES) throw new Error('invalid_mp4_size');
      const file = new File([blob], `startus-chemistry-${Date.now()}.mp4`, { type: 'video/mp4' });
      const objectUrl = URL.createObjectURL(blob);
      if (!mountedRef.current || controller.signal.aborted) {
        URL.revokeObjectURL(objectUrl);
        return false;
      }
      objectUrlRef.current = objectUrl;
      setMp4Blob(blob);
      setMp4File(file);
      setMp4VideoUrl(objectUrl);
      setMp4ExpiresAt(payload.expires_at || '');
      setConversionStatus('ready');
      setConversionError('');
      trackEvent('chemistry_video_convert_complete', {
        analysis_type: 'chemistry', output_size_bytes: blob.size,
      });
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;
      let code = String(error?.message || 'mp4_conversion_failed');
      if (code === 'Failed to fetch' || code === 'NetworkError') code = `mp4_${phase}_failed`;
      if (phase === 'upload' && !error?.status) code = 'mp4_upload_failed';
      if (phase === 'download' && code !== 'mp4_expired') code = 'mp4_download_failed';
      if (code === 'mp4_expired') trackEvent('chemistry_video_expired', { analysis_type: 'chemistry' });
      if (mountedRef.current) {
        setConversionStatus('failed');
        setConversionError(code);
      }
      trackEvent('chemistry_video_convert_fail', {
        analysis_type: 'chemistry', failure_reason: code,
      });
      return false;
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [resetMp4]);

  useEffect(() => () => {
    mountedRef.current = false;
    requestRef.current?.abort();
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  return {
    conversionStatus, conversionError, mp4VideoUrl, mp4ExpiresAt, mp4File, mp4Blob,
    convertToMp4, resetMp4,
  };
}
